
import { Component, Vue } from "vue-property-decorator";
import Sidebar from "@/components/navigations/Sidebar.vue";
import cta from "@/components/buttons/Button.vue";
import tag from "@/components/tags/Tag.vue";
import PanelOption from "@/components/option/Option.vue";
import Counter from "@/components/forms/Counter.vue";
import checkbox from "@/components/checkbox/Checkbox.vue";
import { activites, login, toast, verifAssist } from "@/store";
import { found, select } from "@/helpers";
import {
  Activite,
  LotDetail,
  LotMateriel,
  LotMesVerifications,
  LotMesVerificationsVerification,
  User,
} from "@/models";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

@Component({
  methods: {
    found,
    select,
  },
  components: {
    Sidebar,
    cta,
    tag,
    PanelOption,
    Counter,
    checkbox,
  },
})
/**
 * Guided, one-item-at-a-time equipment verification flow for a single "lot" location.
 * Tracks a `uuid_session` so verifications entered in this session can be told apart from
 * earlier ones on the same lot (see {@link checkVerif}).
 */
export default class VerificationAssisteeLot extends Vue {
  private dataReady = false;
  private withActivite = false;
  private PanelOptionState = false;
  private categorieFocus = 0;
  private materielFocus = -1;
  private counterFocus = 0;
  private electroniqueIsOkFocus = true;
  private nextIsAvailable = true;
  private isFocused = false;

  private uuid_session = uuidv4();

  async mounted(): Promise<void> {
    if (this.$route.params.lot) {
      if (this.$route.params.activite) {
        if (!(await activites.activite(this.$route.params.activite))) {
          toast.pushError(
            "La vérification assistée n'a pas pu être réalisée à partir de l'activité demandée."
          );
        } else {
          this.withActivite = true;
        }
      }
      await verifAssist.lot(this.$route.params.lot);
      await verifAssist.mesVerifications(this.uuid_session ?? undefined);
    }
    this.dataReady = true;
  }

  private date(date: string, format = "DD/MM/YYYY"): string {
    return dayjs(date).format(format);
  }

  /** Opens the guided-verification panel from the start (first category/item). */
  private startVerif(): void {
    this.PanelOptionState = true;
  }

  /** Closes the guided-verification panel and resets its focus state. */
  private closeVerifAssist(): void {
    this.PanelOptionState = false;
    this.categorieFocus = 0;
    this.materielFocus = -1;
    this.counterFocus = 0;
    this.electroniqueIsOkFocus = true;
  }

  /** Jumps the guided panel directly to a specific item (used when clicking an item in the list). */
  private verifyElement(
    materiel: LotMateriel,
    index: number,
    indexCat: number
  ): void {
    if (materiel.quantite > 0) {
      this.categorieFocus = indexCat;
      this.materielFocus = index;
      this.counterFocus = materiel.quantite;
      this.electroniqueIsOkFocus = true;
      this.PanelOptionState = true;
      this.isFocused = true;
    }
  }

  /** Advances the guided panel's focus to the next item, next category, or ends the sequence. */
  private verifNext(): void {
    if (this.lot) {
      if (
        this.lot.categories[this.categorieFocus].materiels.length - 1 >
        this.materielFocus
      ) {
        this.materielFocus++;
        this.counterFocus =
          this.lot.categories[this.categorieFocus].materiels[
            this.materielFocus
          ].quantite;
        this.electroniqueIsOkFocus = true;
      } else if (this.lot.categories.length - 1 > this.categorieFocus) {
        this.categorieFocus++;
        this.materielFocus = -1;
        this.electroniqueIsOkFocus = true;
      } else {
        this.categorieFocus = -1;
        this.electroniqueIsOkFocus = true;
      }
    }
  }

  /**
   * Records the verification result for the currently-focused item, then either closes the panel
   * or advances to the next item, per `closePanel`.
   */
  private async saveVerif(
    session: string,
    uuid: number,
    count: number,
    statutElectronique: boolean | null,
    closePanel?: boolean
  ): Promise<void> {
    this.nextIsAvailable = false;
    if (
      await verifAssist.addVerif({
        session: session,
        lot: this.$route.params.lot,
        activite: this.$route.params.activite ?? undefined,
        uuid: uuid,
        quantite: count,
        statut_electronique: statutElectronique ?? undefined,
      })
    ) {
      await verifAssist.mesVerifications(this.uuid_session ?? undefined);
      if (closePanel) {
        this.isFocused = false;
        this.closeVerifAssist();
      } else {
        this.verifNext();
      }
    }
    this.nextIsAvailable = true;
  }

  /** Returns this session's recorded verification for an item by uuid, or `false` if none yet. */
  private checkVerif(uuid: string): boolean | LotMesVerificationsVerification {
    if (this.mesVerifs && this.mesVerifs[0].session == this.uuid_session) {
      return this.mesVerifs[0].verification.filter(
        (v) => v.materiel.uuid == uuid
      )[0];
    } else {
      return false;
    }
  }

  get context(): User | null {
    return login.getContext;
  }

  get lot(): LotDetail | null {
    return verifAssist.getLot;
  }

  get mesVerifs(): LotMesVerifications[] | null {
    return verifAssist.getMesVerifications;
  }

  get activite(): Activite | null {
    return activites.getActivite;
  }
}
